/* This grid used by the characteristics and outcomes filter panes and handles the particular selection behavior
used by these grids
*/
import {
	all,
	reactive,
	Div,
	Input,
	Span,
	Variable,
	VString,
	VArray
} from 'alkali'
import { Grid } from 'components/grid/Grid'
import { Tree } from 'components/grid/Tree'
import './AttributesGrid.scss'
import { TermEditorIcon } from './TermEditorIcon'
import { faqOrReadOnly, structuredQuery } from 'model/SearchQuery'
import { get } from 'tools/request'
import app from 'app/app'
import { HasFilter } from './HasFilter'

export class AttributesGrid extends HasFilter(Tree(Grid)).with('.attributes-grid', {
	loadingMessage: 'Loading...',
	deselectOnRefresh: false,
}) {
	ready(properties) {
		this.topScore = this.collection.to(collection =>
			Math.max(...collection.map(asScore)))
		this.columns = this.columns || {
			selector: {
				label: '',
				flex: '0 0 18px',
				width: 18,
				selector: (column, selected, cell, data) => {
					let selectedVar = reactive(selected)
					let selectedAs =selectedVar.to(selected => {
						if (!selected)
							return false
						let attributes = structuredQuery.get(this.attributeType + 's')
						for (let i = 0, l = attributes.length; i <l; i++) {
							let attribute = attributes[i]
							let label = attribute.label
							do {
								if (attribute.ids?.includes(data.id) || attribute.id == data.id)
									return {
										position: i + 1,
										altLabel: attribute.label == label && attribute.id == data.id ? null : label
									}
							} while (attribute = attribute.or)
						}
						return {}
					})
					let selector = new Div('.selector-checkbox', {
						classes: { 'selector-selected': selectedVar },
						content: selectedAs.to(selectedAs => selectedAs ? (selectedAs.position > 1 && this.attributeType == 'characteristic') ? selectedAs.position : '✓' : ''),
						title: selectedAs.to(selectedAs =>
								selectedAs 
								? !selectedAs.altLabel && selectedAs.position > 1 
									? 'Additional, required characteristic' 
									: 'Select this for analysis'
								: 'Select this for analysis'
							),
						onclick: (event) => {
							if (faqOrReadOnly.valueOf())
								return
							let row = this.row(event)
							let isSelected = !selectedVar.valueOf()
							if (this.selectionMode != 'toggle') {
								if (isSelected)
									this.clearSelection() // TODO: Remove this once we support multiple outcomes
								if (structuredQuery.outcomes.valueOf()?.length == 0) {
									this.clearSelection()
									isSelected = true
								}
							}
							this.select(row, null, isSelected)
							selectedVar.put(isSelected)
							if (!isSelected && this.attributeType == 'characteristic')
								this.queueRefresh()
						}
					})
					Object.defineProperty(selector, 'checked', {
						set: (checked) => {
							selectedVar.put(checked)
							if (!checked && this.attributeType == 'characteristic')
								this.queueRefresh()
						}
					})
					selector.selectedVar = selectedVar
					cell.append(selector)
					cell.input = selector
				}
			},
			menu: {
				label: '☰',
				flex: '0 0 18px',
				width: 18,
				renderCell: (data, value, td) => {
					Object.assign(td, {
						onpointerdown(event) {
							event.stopPropagation()
						},
						onpointerup(event) {
							event.stopPropagation()
						},
						onclick: (event) => {
							if (faqOrReadOnly.valueOf())
								return
							event.stopPropagation()
							let row = this.row(event)
							const attributesDropdown = new this.Dropdown({
								selected: data,
								attributeType: this.attributeType,
								onSelection: (isSelected) => {
									if (isSelected !== undefined)
										this.select(row, null, isSelected)
									this.row(data).element.querySelector('.selector-checkbox').selectedVar.updated()
									this.queueRefresh()
								}
							})
							attributesDropdown.show(event)
							setTimeout(() => {
								document.dispatchEvent(event) // still fire it to close other dropdowns
							})
						}
					})
					return new Span('.selection-menu-icon', {
						content: '☰',
						title: data.children ? 'Click to customize how this outcome is included in analysis' : 'Click to customize how this condition is included in analysis',
					})
				}
			},
			expand: {
				label: '',
				flex: '0 0 16px',
				width: 16,
				renderExpando: true,
				/*renderCell(data, value, cell) {
					let expanded = reactive(false)
					return new Icon({
						icon: expanded.to(expanded => expanded ? 'chevron-down' : 'chevron-right'),
						onclick: () => {
							let expand = !expanded.valueOf()
							expanded.put(expand)
						}
					})
				}*/
			},
			name: {
				flex: '0.8 0.8 100px',
				width: '80%',
				renderCell(data, name) {
					name = name || data.label
					if (data.qualifiers && data.type != 'Binary')
						name += ' (' + data.type + ')'
					return new Span(/*data.masterId ? */'.digitized-outcome'/* : '.undigitized-outcome'*/, {
						content: name,
						title: data.definition || name,
					})
				}
			},
			editConcept: {
				flex: '0 0 20px',
				width: 20,
				renderCell(data) {
					if (app.user?.FeatureConfig?.['Bindings Admin']?.Enabled)
						return new TermEditorIcon({ term: data, type: this.grid.attributeType })
				}
			}
		}
/* I don't think we really want to expand just when clicking on a row
		this.on('.dgrid-row:click', event => {
			if (event.target.classList.contains('dgrid-expando-icon'))
				return
			let row = this.row(event)
			if (!this._expanded[row.id]) {
				this.expand(row)
			}
		})*/
		super.ready(properties)
	}
	renderRow(item, options) {
		let row = super.renderRow(item, options)
		row.append(Div('.attribute-usage-bar', {
			width: asScore(item) / this.topScore * 90 + '%'
		}))
		return row
	}
	_handleSelect(event, target) {
		if (faqOrReadOnly.valueOf())
			return
		return super._handleSelect(event, target)
	}
	_extendedSelectionHandler(event, target) {
		// if we only have auto-selections, clear them when explicit selections are made
		if (!(structuredQuery.valueOf()?.[this.attributeType + 's']?.length > 0)) {
			this.clearSelection()
		}
		let rowEntry = this.row(event)
		let wasSelected = this.selection[rowEntry.id]
		if (event.target.classList.contains('dgrid-expando-icon') && wasSelected)
			return
		let ids = [ rowEntry.id ]
		let term = rowEntry.data
		// also auto select any synonym children
		if (term.synonyms) {
			for (let child of rowEntry.data?.children || [])
				if (term.synonyms.includes(child.id))
					ids.push(child.id)
		}
		term.ids = ids
		if (!wasSelected) {
			this.selected.put([term.id.split(',')[0]])
		}
		else {
			for (let id of ids) {
				this.select(id, null, false)
			}
		}
	}
	shouldExpand(row, level, previouslyExpanded) {
		let ids = this.selected.valueOf()
		return previouslyExpanded || ids && ids.some(id => id && id.startsWith(row.id) &&
			(id.length > row.id.length || row.data.children?.some(child => ids.includes(child.id))))
	}
	getChildren(data) {
		return data.children
	}
	mayHaveChildren(data) {
		return data.children
	}
	getIdentity(data) {
		return data.id
	}
}

const asScore = entry => {
	return Math.sqrt(entry.count ? entry.count * 20 : entry.score || 0)
}
